import type { SiteTimelineView, TimelineMetric, TimelinePoint } from '@/types/api';

/**
 * A figure's recent history from the timeline (ADR-0054), for the SEO tiles
 * (ADR-0057): the best source's points, what the figure was a month ago, and
 * the values for the small line.
 */

/** The source preferred where several give the same figure: the richest history first. */
const SOURCE_ORDER = ['google', 'ahrefs', 'dataforseo', 'openpagerank'];

export function pointsOf(view: SiteTimelineView | undefined, metric: TimelineMetric, sources: readonly string[] = SOURCE_ORDER): readonly TimelinePoint[] {
  const series = (view?.series ?? []).filter((line) => line.metric === metric && sources.includes(line.source) && line.points.length > 0);
  series.sort((a, b) => sources.indexOf(a.source) - sources.indexOf(b.source));
  return series[0]?.points ?? [];
}

export interface Trend {
  readonly current: number | null;
  /** The value about `days` ago, the nearest point at or before then; null when the history is shorter. */
  readonly before: number | null;
  /** The values over the last year, for the small line. */
  readonly history: readonly number[];
}

export function trendOf(points: readonly TimelinePoint[], days = 30, now = Date.now()): Trend {
  if (points.length === 0) return { current: null, before: null, history: [] };
  const since = new Date(now - days * 86_400_000).toISOString().slice(0, 10);
  const yearAgo = new Date(now - 365 * 86_400_000).toISOString().slice(0, 10);
  const before = [...points].reverse().find((point) => point.date <= since) ?? null;
  return {
    current: points[points.length - 1]!.value,
    before: before?.value ?? null,
    history: points.filter((point) => point.date >= yearAgo).map((point) => point.value),
  };
}

/** A change as the tile shows it: «+12», «−3,5», «+8%». */
export function deltaOf(current: number | null, before: number | null, options: { percent?: boolean; digits?: number } = {}): { value: number; text: string } | null {
  if (current === null || before === null) return null;
  const change = current - before;
  if (options.percent) {
    if (before === 0) return null;
    const share = Math.round((change / before) * 100);
    return { value: share, text: `${share > 0 ? '+' : share < 0 ? '−' : ''}${Math.abs(share)}%` };
  }
  const rounded = Number(change.toFixed(options.digits ?? 0));
  return { value: rounded, text: `${rounded > 0 ? '+' : rounded < 0 ? '−' : ''}${Math.abs(rounded).toLocaleString('ru-RU')}` };
}

export const number = (value: number | null | undefined, digits = 0) =>
  value === null || value === undefined ? null : value.toLocaleString('ru-RU', { maximumFractionDigits: digits });
